import React, { useState } from 'react'
import {
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material'
import {
  DateRange as DateRangeIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material'
import { TIME_RANGES } from '../../utils/chartUtils'

const TimeRangeSelector = ({ 
  selectedRange, 
  onRangeChange, 
  customStartDate, 
  customEndDate,
  onCustomDateChange,
  disabled = false 
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [customDialogOpen, setCustomDialogOpen] = useState(false)
  const [tempStartDate, setTempStartDate] = useState('')
  const [tempEndDate, setTempEndDate] = useState('')

  const handleRangeClick = (rangeKey) => {
    if (disabled) return
    
    if (rangeKey === 'CUSTOM') {
      setTempStartDate(customStartDate ? customStartDate.toISOString().split('T')[0] : '')
      setTempEndDate(customEndDate ? customEndDate.toISOString().split('T')[0] : '')
      setCustomDialogOpen(true)
    } else {
      onRangeChange(rangeKey)
    }
  }

  const handleCustomDateSubmit = () => {
    if (tempStartDate && tempEndDate) {
      const startDate = new Date(tempStartDate)
      const endDate = new Date(tempEndDate)
      
      // Validate dates
      if (startDate <= endDate) {
        onCustomDateChange(startDate, endDate)
        onRangeChange('CUSTOM')
        setCustomDialogOpen(false)
      }
    }
  }

  const formatCustomRangeLabel = () => {
    if (!customStartDate || !customEndDate) return 'Custom'
    
    const start = customStartDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: customStartDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    })
    const end = customEndDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: customEndDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    })
    
    return `${start} - ${end}`
  }

  const timeRangeEntries = Object.entries(TIME_RANGES)
  const customLabel = selectedRange === 'CUSTOM' ? formatCustomRangeLabel() : 'Custom'

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
          Time Range
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Preset time ranges */}
          {timeRangeEntries.map(([key, config]) => (
            <Chip
              key={key}
              label={isMobile ? key : config.label}
              variant={selectedRange === key ? 'filled' : 'outlined'}
              color={selectedRange === key ? 'primary' : 'default'}
              onClick={() => handleRangeClick(key)}
              disabled={disabled}
              sx={{
                borderRadius: 2,
                fontWeight: selectedRange === key ? 600 : 400,
                minWidth: isMobile ? 45 : 'auto',
                '&:hover': {
                  backgroundColor: selectedRange === key ? 'primary.main' : 'action.hover'
                }
              }}
            />
          ))}
          
          {/* Custom range button */}
          <Chip
            label={customLabel}
            variant={selectedRange === 'CUSTOM' ? 'filled' : 'outlined'}
            color={selectedRange === 'CUSTOM' ? 'primary' : 'default'}
            icon={<DateRangeIcon />}
            onClick={() => handleRangeClick('CUSTOM')}
            disabled={disabled}
            sx={{
              borderRadius: 2,
              fontWeight: selectedRange === 'CUSTOM' ? 600 : 400,
              maxWidth: isMobile ? 120 : 200,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              },
              '&:hover': {
                backgroundColor: selectedRange === 'CUSTOM' ? 'primary.main' : 'action.hover'
              }
            }}
          />
        </Box>
      </Box>

      {/* Custom Date Range Dialog */}
      <Dialog 
        open={customDialogOpen} 
        onClose={() => setCustomDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon color="primary" />
            <Typography variant="h6" component="div">
              Custom Date Range
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              label="Start Date"
              type="date"
              value={tempStartDate}
              onChange={(e) => setTempStartDate(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
            
            <TextField
              label="End Date"
              type="date"
              value={tempEndDate}
              onChange={(e) => setTempEndDate(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: tempStartDate || undefined
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
            
            {tempStartDate && tempEndDate && new Date(tempStartDate) > new Date(tempEndDate) && (
              <Typography variant="body2" color="error">
                Start date must be before end date
              </Typography>
            )}
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setCustomDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCustomDateSubmit}
            variant="contained"
            disabled={!tempStartDate || !tempEndDate || new Date(tempStartDate) > new Date(tempEndDate)}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
            }}
          >
            Apply Range
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TimeRangeSelector